import { useState } from "react";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const SEVERITIES = ["Informational", "Low", "Medium", "High", "Critical"];
const TYPES = ["unauthorized_account_access", "credential_compromise", "suspicious_data_access", "data_exfiltration", "privilege_escalation", "malicious_api_activity", "compromised_webhook_or_integration", "third_party_provider_breach", "malware_or_unsafe_document", "lost_device_or_session_compromise", "insider_risk", "database_exposure", "accidental_disclosure", "ransomware_or_destructive", "other"];

export const CreateIncidentDialog = ({ open, onOpenChange, onCreated }) => {
  const [f, setF] = useState({ title: "", incident_type: "unauthorized_account_access", severity: "Medium", description: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    if (busy) return;
    if (!f.title.trim()) { setError("A title is required."); return; }
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/admin/security/incidents", f);
      toast.success(`Incident opened (${data.severity}).`);
      setF({ title: "", incident_type: "unauthorized_account_access", severity: "Medium", description: "" });
      onOpenChange(false); onCreated?.(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(d?.error === "mfa_required" ? "An MFA-verified senior session is required." : formatApiError(d) || "Failed.");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-lg" data-testid="create-incident-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldAlert size={18} className="text-red-600" /> Open incident</DialogTitle>
          <DialogDescription>Opens as a SUSPECTED incident in <b>reported</b>. Only an authorized human can confirm or resolve it.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-1">
          <div className="grid gap-1.5"><Label>Title <span className="text-red-500">*</span></Label>
            <Input data-testid="incident-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Suspected privileged account compromise" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Type</Label>
              <Select value={f.incident_type} onValueChange={(v) => setF({ ...f, incident_type: v })}>
                <SelectTrigger data-testid="incident-type"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid gap-1.5"><Label>Severity</Label>
              <Select value={f.severity} onValueChange={(v) => setF({ ...f, severity: v })}>
                <SelectTrigger data-testid="incident-severity"><SelectValue /></SelectTrigger>
                <SelectContent>{SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="grid gap-1.5"><Label>Description</Label>
            <Textarea data-testid="incident-description" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          {error && <p className="text-sm text-red-600" data-testid="create-incident-error">{error}</p>}
        </div>
        <DialogFooter>
          <button data-testid="create-incident-cancel" disabled={busy} onClick={() => onOpenChange(false)} className="h-9 px-3 rounded-md border border-slate-200 text-sm disabled:opacity-40">Cancel</button>
          <button data-testid="create-incident-submit" disabled={busy || !f.title.trim()} onClick={submit} className="h-9 px-4 rounded-md bg-red-600 text-white text-sm disabled:opacity-50">{busy ? "Opening…" : "Open incident"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
